// src/pages/GeneralSettings.js

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  applyAppearance,
  getStoredAppearance,
  saveAppearance,
} from "../utils/appearancePreference";

import {
  getStoredTimezone,
  getTimezoneOptions,
  saveTimezone,
} from "../utils/timezonePreference";

import "../styles/GeneralSettings.css";

import "flag-icons/css/flag-icons.min.css";

const LANG_KEY =
  "app.lang";

const NOTIFS_KEY =
  "app.notifications";

const languages = [
  {
    code:
      "en",
    label:
      "English",
  },
  {
    code:
      "es",
    label:
      "Español",
  },
  {
    code:
      "fr",
    label:
      "Français",
  },
  {
    code:
      "pt",
    label:
      "Português",
  },
];

const langToFlag = {
  en:
    "us",

  es:
    "es",

  fr:
    "fr",

  pt:
    "pt",
};

function FlagIcon({
  langCode,
}) {
  const flagCode =
    langToFlag[
      langCode
    ] ||
    "us";

  return (
    <span
      className={`fi fi-${flagCode} lang-flag`}
      aria-hidden="true"
    />
  );
}

function LanguageSelect({
  value,
  onChange,
  options,
  ariaLabel,
}) {
  const [
    open,
    setOpen,
  ] = useState(
    false
  );

  const ref =
    useRef(
      null
    );

  const current =
    options.find(
      (
        option
      ) =>
        option.code ===
        value
    ) ||
    options[0];

  useEffect(
    () => {
      const onDocumentClick =
        (
          event
        ) => {
          if (
            !ref.current
          ) {
            return;
          }

          if (
            !ref.current.contains(
              event.target
            )
          ) {
            setOpen(
              false
            );
          }
        };

      document.addEventListener(
        "mousedown",
        onDocumentClick
      );

      return () => {
        document.removeEventListener(
          "mousedown",
          onDocumentClick
        );
      };
    },
    []
  );

  const toggle =
    () => {
      setOpen(
        (
          previous
        ) =>
          !previous
      );
    };

  const select =
    (
      code
    ) => {
      onChange(
        code
      );

      setOpen(
        false
      );
    };

  const onKeyDown =
    (
      event
    ) => {
      if (
        event.key ===
        "Escape"
      ) {
        setOpen(
          false
        );
      }

      if (
        event.key ===
          "Enter" &&
        !open
      ) {
        setOpen(
          true
        );
      }
    };

  return (
    <div
      className="lang-select"
      ref={
        ref
      }
      onKeyDown={
        onKeyDown
      }
    >
      <button
        type="button"
        className="lang-select__button"
        aria-haspopup="listbox"
        aria-expanded={
          open
        }
        aria-label={
          ariaLabel
        }
        onClick={
          toggle
        }
      >
        <FlagIcon
          langCode={
            current.code
          }
        />

        <span className="lang-label">
          {
            current.label
          }
        </span>

        <span
          className="gs-select-caret"
          aria-hidden="true"
        >
          ▾
        </span>
      </button>

      {open && (
        <ul
          className="lang-select__list"
          role="listbox"
          aria-label={
            ariaLabel
          }
        >
          {options.map(
            (
              option
            ) => (
              <li
                key={
                  option.code
                }
                role="option"
                aria-selected={
                  option.code ===
                  value
                }
                className={`lang-select__option ${
                  option.code ===
                  value
                    ? "is-active"
                    : ""
                }`}
                onClick={() =>
                  select(
                    option.code
                  )
                }
                tabIndex={
                  0
                }
                onKeyDown={(
                  event
                ) => {
                  if (
                    event.key ===
                    "Enter"
                  ) {
                    select(
                      option.code
                    );
                  }
                }}
              >
                <FlagIcon
                  langCode={
                    option.code
                  }
                />

                <span className="lang-option-label">
                  {
                    option.label
                  }
                </span>
              </li>
            )
          )}
        </ul>
      )}
    </div>
  );
}

export default function GeneralSettings() {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "general_settings"
    );

  const [
    lang,
    setLang,
  ] = useState(
    () =>
      localStorage.getItem(
        LANG_KEY
      ) ||
      "en"
  );

  const [
    appearance,
    setAppearance,
  ] = useState(
    getStoredAppearance
  );

  const [
    timezone,
    setTimezone,
  ] = useState(
    getStoredTimezone
  );

  const [
    notificationsActive,
    setNotificationsActive,
  ] = useState(
    () => {
      const saved =
        localStorage.getItem(
          NOTIFS_KEY
        );

      return saved
        ? saved ===
            "active"
        : true;
    }
  );

  const timezoneOptions =
    useMemo(
      () =>
        getTimezoneOptions(),
      []
    );

  /* =========================================================
     Language
     ========================================================= */

  useEffect(
    () => {
      localStorage.setItem(
        LANG_KEY,
        lang
      );

      document.documentElement.setAttribute(
        "lang",
        lang
      );

      i18n.changeLanguage(
        lang
      );
    },
    [
      lang,
      i18n,
    ]
  );

  /* =========================================================
     Appearance
     ========================================================= */

  useEffect(
    () => {
      applyAppearance(
        appearance
      );
    },
    [
      appearance,
    ]
  );

  useEffect(() => {
    const syncAppearance = () => {
      setAppearance(getStoredAppearance());
    };

    window.addEventListener("app:appearance:changed", syncAppearance);

    return () => {
      window.removeEventListener("app:appearance:changed", syncAppearance);
    };
  }, []);

  const handleAppearanceChange = (event) => {
    const value = event.target.value;

    setAppearance(value);
    saveAppearance(value);

    window.dispatchEvent(
      new CustomEvent("app:appearance:changed", {
        detail: { value },
      })
    );
  };

  /* =========================================================
     Timezone
     ========================================================= */

  const handleTimezoneChange =
    (
      event
    ) => {
      const value =
        event.target
          .value;

      setTimezone(
        value
      );

      saveTimezone(
        value
      );
    };

  /* =========================================================
     Notifications
     ========================================================= */

  useEffect(
    () => {
      const value =
        notificationsActive
          ? "active"
          : "inactive";

      localStorage.setItem(
        NOTIFS_KEY,
        value
      );

      if (
        notificationsActive &&
        "Notification" in
          window &&
        Notification.permission ===
          "default"
      ) {
        Notification.requestPermission()
          .catch(
            () => {}
          );
      }

      window.dispatchEvent(
        new CustomEvent(
          "app:notifications:changed",
          {
            detail: {
              active:
                notificationsActive,

              value,
            },
          }
        )
      );
    },
    [
      notificationsActive,
    ]
  );

  return (
    <div className="general-settings">
      <div className="gs-header">
        <h1>
          {t(
            "title"
          )}
        </h1>

        <p>
          {t(
            "subtitle"
          )}
        </p>
      </div>

      <div className="gs-card">
        {/* =====================================================
            Language
            ===================================================== */}

        <div className="gs-row">
          <div className="gs-label">
            <div className="gs-title">
              {t(
                "language.title"
              )}
            </div>

            <div className="gs-sub">
              {t(
                "language.subtitle"
              )}
            </div>
          </div>

          <div className="gs-control">
            <LanguageSelect
              value={
                lang
              }
              onChange={
                setLang
              }
              options={
                languages
              }
              ariaLabel={t(
                "language.aria_choose"
              )}
            />
          </div>
        </div>

        <div className="gs-divider" />

        {/* =====================================================
            Appearance
            ===================================================== */}

        <div className="gs-row">
          <div className="gs-label">
            <div className="gs-title">
              {t(
                "appearance.title"
              )}
            </div>

            <div className="gs-sub">
              {t(
                "appearance.subtitle"
              )}
            </div>
          </div>

          <div className="gs-control">
            <div className="gs-native-select-wrap">
              <select
                className="gs-select"
                value={
                  appearance
                }
                onChange={
                  handleAppearanceChange
                }
                aria-label={t(
                  "appearance.aria_choose"
                )}
              >
                <option value="dark">
                  {t(
                    "appearance.options.dark"
                  )}
                </option>

                <option value="light">
                  {t(
                    "appearance.options.light"
                  )}
                </option>

                <option value="system">
                  {t(
                    "appearance.options.system"
                  )}
                </option>
              </select>

              <span
                className="gs-native-select-caret"
                aria-hidden="true"
              >
                ▾
              </span>
            </div>
          </div>
        </div>

        <div className="gs-divider" />

        {/* =====================================================
            Timezone
            ===================================================== */}

        <div className="gs-row">
          <div className="gs-label">
            <div className="gs-title">
              {t(
                "timezone.title"
              )}
            </div>

            <div className="gs-sub">
              {t(
                "timezone.subtitle"
              )}
            </div>
          </div>

          <div className="gs-control">
            <div className="gs-native-select-wrap">
              <select
                className="gs-select"
                value={
                  timezone
                }
                onChange={
                  handleTimezoneChange
                }
                aria-label={t(
                  "timezone.aria_choose"
                )}
              >
                {timezoneOptions.map(
                  (
                    option
                  ) => (
                    <option
                      key={
                        option
                      }
                      value={
                        option
                      }
                    >
                      {
                        option
                      }
                    </option>
                  )
                )}
              </select>

              <span
                className="gs-native-select-caret"
                aria-hidden="true"
              >
                ▾
              </span>
            </div>
          </div>
        </div>

        <div className="gs-divider" />

        {/* =====================================================
            Notifications
            ===================================================== */}

        <div className="gs-row">
          <div className="gs-label">
            <div className="gs-title">
              {t(
                "notifications.title"
              )}
            </div>

            <div className="gs-sub">
              {t(
                "notifications.subtitle"
              )}
            </div>
          </div>

          <div className="gs-control">
            <label className="gs-switch">
              <input
                type="checkbox"
                checked={
                  notificationsActive
                }
                onChange={(
                  event
                ) =>
                  setNotificationsActive(
                    event
                      .target
                      .checked
                  )
                }
              />

              <span className="gs-slider" />
            </label>

            <span className="gs-switch-text">
              {notificationsActive
                ? t(
                    "notifications.active"
                  )
                : t(
                    "notifications.inactive"
                  )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}